import { useId } from "react";
import type { FormEvent, MouseEvent, ReactNode } from "react";
import { X } from "lucide-react";
import { CertificationIconButton } from "./CertificationIconButton";

type Props = {
  open: boolean;
  title: string;
  eyebrow?: string;
  description?: string;
  size?: "default" | "wide";
  children: ReactNode;
  footer: ReactNode;
  onClose: () => void;
  onSubmit?: () => void;
  submitting?: boolean;
  error?: string | null;
  closeOnBackdrop?: boolean;
};

export function CertificationModal({
  open,
  title,
  eyebrow = "Certificaciones",
  description,
  size = "default",
  children,
  footer,
  onClose,
  onSubmit,
  submitting = false,
  error = null,
  closeOnBackdrop = true,
}: Props) {
  const titleId = useId();

  if (!open) return null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit?.();
  };

  const handleBackdropMouseDown = () => {
    if (closeOnBackdrop && !submitting) onClose();
  };

  const content = (
    <>
      <div className="iso27001-modal-header">
        <div>
          <div className="iso27001-modal-eyebrow">{eyebrow}</div>
          <h3 id={titleId}>{title}</h3>
          {description ? <p>{description}</p> : null}
        </div>
        <CertificationIconButton
          label="Cerrar"
          icon={<X size={18} />}
          requiresManage={false}
          disabled={submitting}
          onClick={onClose}
        />
      </div>
      {error ? <div role="alert" style={{ color: "#b91c1c", fontWeight: 800 }}>{error}</div> : null}
      <div className="iso27001-modal-body">{children}</div>
      <div className="iso27001-modal-actions">{footer}</div>
    </>
  );

  const modalClassName = `iso27001-modal ${size === "wide" ? "iso27001-modal-wide" : ""}`.trim();

  return (
    <div className="iso27001-modal-backdrop" role="presentation" onMouseDown={handleBackdropMouseDown}>
      {onSubmit ? (
        <form
          className={modalClassName}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onSubmit={handleSubmit}
          onMouseDown={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
        >
          {content}
        </form>
      ) : (
        <div
          className={modalClassName}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onMouseDown={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
        >
          {content}
        </div>
      )}
    </div>
  );
}
